"use client";

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";

const TOKEN_KEY = "psx_auth_token";
const EMAIL_KEY = "psx_auth_email";
const AUTH_EVENT = "psx-auth-changed";
const LEGACY_PORTFOLIO_KEY = "psx_portfolio_v1";

function portfolioKey(email: string) {
  return `psx_portfolio_${email.trim().toLowerCase()}`;
}

interface AuthContextValue {
  email: string | null;
  token: string | null;
  isLoggedIn: boolean;
  isReady: boolean;
  login: (token: string, email: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readEmail(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(EMAIL_KEY);
}

function readToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // localStorage is unavailable during SSR. Hydrate it after the first client render.
  const [email, setEmail] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  const sync = useCallback(() => {
    setEmail(readEmail());
    setToken(readToken());
  }, []);

  useEffect(() => {
    const hydrate = window.setTimeout(() => {
      sync();
      setIsReady(true);
    }, 0);
    window.addEventListener("storage", sync);
    window.addEventListener(AUTH_EVENT, sync);
    return () => {
      window.clearTimeout(hydrate);
      window.removeEventListener("storage", sync);
      window.removeEventListener(AUTH_EVENT, sync);
    };
  }, [sync]);

  const login = useCallback((token: string, userEmail: string) => {
    localStorage.removeItem(LEGACY_PORTFOLIO_KEY);
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(EMAIL_KEY, userEmail);
    setEmail(userEmail);
    setToken(token);
    window.dispatchEvent(new Event(AUTH_EVENT));
  }, []);

  const logout = useCallback(() => {
    const currentEmail = readEmail();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(LEGACY_PORTFOLIO_KEY);
    if (currentEmail) localStorage.removeItem(portfolioKey(currentEmail));
    setEmail(null);
    setToken(null);
    window.dispatchEvent(new Event(AUTH_EVENT));
    window.location.href = "/login";
  }, []);

  return (
    <AuthContext.Provider value={{ email, token, isLoggedIn: !!email && !!token, isReady, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

const SSR_DEFAULT: AuthContextValue = {
  email: null,
  token: null,
  isLoggedIn: false,
  isReady: false,
  login: () => {},
  logout: () => {},
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  return ctx ?? SSR_DEFAULT;
}
